<template>
  <div class="container-fluid certificate-container py-4">
    <div class="row g-4">
      <!-- Sidebar de cursos -->
      <div class="col-12 col-lg-4 courses-sidebar">
        <div class="courses-wrapper p-4">
          <!-- Estado sin cursos -->
          <div v-if="products.length == 0" class="empty-state text-center py-5">
            <i class="bi bi-journal-x mb-3 d-block" style="font-size: 48px; color: #A1A1AA;"></i>
            <h5 class="empty-title mb-0">Aún no ha adquirido un curso</h5>
          </div>

          <!-- Lista de cursos -->
          <template v-else>
            <h4 class="sidebar-main-title mb-4 pb-2 border-bottom-subtle">Mis Certificaciones</h4>
            <div v-if="coursesWithCertificate.length === 0" class="alert-no-certificate p-3">
              <div class="d-flex align-center">
                <i class="bi bi-info-circle me-2" style="font-size: 20px; color: #F59E0B;"></i>
                <span class="alert-desc">No tiene cursos con certificado disponible.</span>
              </div>
            </div>
            <div v-else class="courses-list">
              <div
                v-for="product in coursesWithCertificate"
                :key="product.id"
                :class="['course-card-item', { active: productSelected && productSelected.id === product.id }]"
                @click="getCertificateInfo(product)"
              >
                <div class="course-card-row">
                  <div class="course-img-wrapper flex-shrink-0">
                    <img
                      :src="product.url_portada"
                      class="course-image"
                      alt="Portada del curso"
                      @error="onImgError"
                    />
                  </div>
                  <div class="course-info-content overflow-hidden">
                    <h6 class="course-card-title text-truncate">
                      {{ product.title }}
                    </h6>
                    <span class="certificate-badge">
                      <i class="bi bi-award"></i>
                      Con certificado
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Contenido principal -->
      <div class="col-12 col-lg-8">
        <div class="certificate-content p-4">
          <!-- Estado de espera sin curso seleccionado -->
          <div v-if="waitSelection" class="empty-certificate-selection text-center py-5 my-auto">
            <div class="empty-icon-circle mb-3 mx-auto">
              <i class="bi bi-award" style="font-size: 48px; color: var(--primary-color);"></i>
            </div>
            <h4 class="empty-title mb-2">Selecciona un curso</h4>
            <p class="empty-subtitle mb-0">
              Elige una de las certificaciones de la lista lateral para consultar los requisitos y descargar tu certificado digital.
            </p>
          </div>

          <!-- Detalles del certificado -->
          <div v-else class="certificate-details">
            <!-- Cabecera del curso seleccionado -->
            <div class="selected-course-header p-3 mb-4">
              <div class="d-flex align-items-center">
                <div class="selected-img-wrapper me-3 flex-shrink-0">
                  <img
                    :src="productSelected.url_portada"
                    class="selected-course-image"
                    alt="Portada del curso seleccionado"
                    @error="onImgError"
                  />
                </div>
                <div class="overflow-hidden">
                  <h3 class="selected-course-title mb-0 text-truncate">{{ productSelected.title }}</h3>
                </div>
              </div>
            </div>

            <!-- Cargando información del certificado -->
            <div v-if="loadingCertificateInfo" class="loading-certificate-info text-center py-5">
              <div class="spinner-border text-success mb-3" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
              <p class="text-muted mb-0">Cargando información del certificado...</p>
            </div>

            <!-- Información del certificado -->
            <div v-else-if="certificateInfo && !certificateInfo.noCertificate" class="certificate-info">
              <div class="card">
                <div class="card-body">
                  <h4 class="card-title mb-4">Información del Certificado</h4>

                  <!-- Grid de información -->
                  <div class="row g-4 mb-5">
                    <div class="col-md-6">
                      <div class="info-item">
                        <label class="text-muted mb-1">Condición</label>
                        <p class="mb-0 fw-bold">
                          {{
                            getConditionText(
                              certificateInfo.condition_to_certificate
                            )
                          }}
                        </p>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="info-item">
                        <label class="text-muted mb-1">Tipo</label>
                        <p class="mb-0 fw-bold">
                          {{
                            certificateInfo.type_certificate === 1
                              ? "Gratuito"
                              : "De pago"
                          }}
                        </p>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="info-item">
                        <label class="text-muted mb-1">Validado por</label>
                        <p class="mb-0 fw-bold">
                          {{ getValidatedByText(certificateInfo.validated_by) }}
                        </p>
                      </div>
                    </div>
                    <div v-if="certificateInfo.validated_by === 'module'" class="col-md-6">
                      <div class="info-item">
                        <label class="text-muted mb-1">Módulo</label>
                        <p class="mb-0 fw-bold">
                          {{ certificateInfo.module_name }}
                        </p>
                      </div>
                    </div>
                    <div v-if="certificateInfo.validated_by === 'lesson'" class="col-md-6">
                      <div class="info-item">
                        <label class="text-muted mb-1">Clase</label>
                        <p class="mb-0 fw-bold">
                          {{ certificateInfo.lesson_name }}
                        </p>
                      </div>
                    </div>
                    <div v-if="certificateInfo.type_certificate === 0" class="col-md-6">
                      <div class="info-item">
                        <label class="text-muted mb-1">Precio</label>
                        <p class="mb-0 fw-bold">
                          ${{ certificateInfo.certificate_price }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Sección de progreso -->
                  <div class="progress-section">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <h5 class="mb-0">Progreso del Curso</h5>
                      
                      <!-- Botones para alternar vista - Solo mostrar si es validado por módulo -->
                      <div v-if="certificateInfo.validated_by === 'module'" class="btn-group" role="group">
                        <button 
                          type="button" 
                          class="btn btn-sm"
                          :class="progressView === 'course' ? 'btn-primary' : 'btn-outline-primary'"
                          @click="setProgressView('course')"
                        >
                          <i class="bi bi-mortarboard me-1"></i>
                          Por Curso
                        </button>
                        <button 
                          type="button" 
                          class="btn btn-sm"
                          :class="progressView === 'modules' ? 'btn-primary' : 'btn-outline-primary'"
                          @click="setProgressView('modules')"
                        >
                          <i class="bi bi-list-nested me-1"></i>
                          Por Módulos
                        </button>
                      </div>
                    </div>

                    <!-- Vista por curso (original) -->
                    <div v-if="progressView === 'course'" class="course-progress-view">
                      <div class="progress-wrapper mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="fw-bold">Progreso total</span>
                          <span class="badge bg-success">{{ combinedProgress }}%</span>
                        </div>
                        <div class="progress" style="height: 10px">
                          <div class="progress-bar bg-success" role="progressbar"
                            :style="{ width: combinedProgress + '%' }" :aria-valuenow="combinedProgress" aria-valuemin="0"
                            aria-valuemax="100"></div>
                        </div>
                      </div>

                      <div class="additional-progress small text-muted">
                        <p v-if="showVideoProgress" class="mb-1">
                          <i class="bi bi-play-circle me-2"></i>
                          Progreso de videos: {{ currentCourseProgress }}%
                        </p>
                        <p v-if="showExamProgress" class="mb-1">
                          <i class="bi bi-pencil-square me-2"></i>
                          Progreso de exámenes: {{ certificate_exam_progress }}%
                        </p>
                      </div>
                    </div>

                    <!-- Vista por módulos (nueva) - Solo mostrar si es validado por módulo -->
                    <div v-else-if="progressView === 'modules' && certificateInfo.validated_by === 'module'" class="modules-progress-view">
                      
                      <!-- Botón para actualizar progreso de módulos -->
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <small class="text-muted">
                          <i class="bi bi-info-circle me-1"></i>
                          Progreso detallado por cada módulo del curso
                        </small>
                        <button 
                          class="btn btn-sm btn-outline-secondary" 
                          @click="loadModuleProgress"
                          :disabled="loadingModuleProgress"
                        >
                          <i class="bi bi-arrow-clockwise me-1" :class="{ 'fa-spin': loadingModuleProgress }"></i>
                          {{ loadingModuleProgress ? 'Cargando...' : 'Actualizar' }}
                        </button>
                      </div>

                      <!-- Estado de carga -->
                      <div v-if="loadingModuleProgress" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span class="text-muted">Cargando progreso de módulos...</span>
                      </div>

                      <!-- Resumen general de módulos -->
                      <div v-else-if="moduleProgressData" class="modules-summary mb-4">
                        <div class="row g-3">
                          <div class="col-md-4">
                            <div class="summary-card text-center p-3 bg-light rounded">
                              <h3 class="text-primary mb-1">{{ moduleProgressData.summary.total_modules }}</h3>
                              <small class="text-muted">Total Módulos</small>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="summary-card text-center p-3 bg-success text-white rounded">
                              <h3 class="mb-1">{{ moduleProgressData.summary.completed_modules }}</h3>
                              <small>Completados</small>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="summary-card text-center p-3 bg-warning text-white rounded">
                              <h3 class="mb-1">{{ moduleProgressData.summary.total_modules - moduleProgressData.summary.completed_modules }}</h3>
                              <small>Pendientes</small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Lista detallada de módulos -->
                      <div v-if="moduleProgressData && moduleProgressData.modules" class="modules-detailed">
                        <div v-for="(module, index) in moduleProgressData.modules" :key="module.module_id" 
                             class="module-item mb-3">
                          <div class="card border-0 shadow-sm">
                            <div class="card-body">
                              <div class="row align-items-center">
                                <div class="col-md-6">
                                  <div class="d-flex align-items-center">
                                    <div class="module-status-icon me-3">
                                      <i :class="module.is_completed ? 'bi bi-check-circle-fill text-success' : 'bi bi-circle text-muted'" 
                                         style="font-size: 1.5rem;"></i>
                                    </div>
                                    <div>
                                      <h6 class="mb-1 fw-bold">
                                        {{ index + 1 }}. {{ module.module_name }}
                                      </h6>
                                      <small class="text-muted">
                                        ID: {{ module.module_id }} | 
                                        {{ module.completed_classes }}/{{ module.total_classes }} clases
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <div class="col-md-2">
                                  <div class="progress" style="height: 8px;">
                                    <div class="progress-bar" 
                                         :class="module.is_completed ? 'bg-success' : 'bg-warning'"
                                         :style="{ width: module.completion_percentage + '%' }"></div>
                                  </div>
                                  <small class="text-muted">{{ module.completion_percentage }}%</small>
                                </div>
                                <div class="col-md-2 text-center">
                                  <span class="badge" 
                                        :class="module.is_completed ? 'bg-success' : 'bg-warning'">
                                    {{ module.is_completed ? 'Completado' : 'Pendiente' }}
                                  </span>
                                </div>
                                <div class="col-md-2 text-end">
                                  <!-- Botón de descarga del certificado por módulo - Solo si es validado por módulo -->
                                  <button 
                                    v-if="module.is_completed && certificateInfo.validated_by === 'module'"
                                    class="btn btn-sm btn-outline-success"
                                    @click="downloadModuleCertificate(module.module_id)"
                                    :disabled="downloadingModuleCertificate === module.module_id"
                                    title="Descargar certificado del módulo"
                                  >
                                    <i v-if="downloadingModuleCertificate === module.module_id" 
                                       class="bi bi-hourglass-split"></i>
                                    <i v-else class="bi bi-download"></i>
                                  </button>
                                  <small v-else-if="!module.is_completed || certificateInfo.validated_by !== 'module'" class="text-muted d-block">
                                    <i class="bi bi-lock me-1"></i>
                                    No disponible
                                  </small>
                                </div>
                              </div>

                              <!-- Detalles expandibles -->
                              <div v-if="!module.is_completed && (module.pending_class_ids.length > 0)" 
                                   class="mt-3 pt-3 border-top">
                                <details>
                                  <summary class="text-muted small cursor-pointer">
                                    <i class="bi bi-chevron-right me-1"></i>
                                    Ver clases pendientes ({{ module.pending_class_ids.length }})
                                  </summary>
                                  <div class="mt-2">
                                    <div class="d-flex flex-wrap gap-1">
                                      <span v-for="classId in module.pending_class_ids" :key="classId"
                                            class="badge bg-light text-dark border small">
                                        Clase ID: {{ classId }}
                                      </span>
                                    </div>
                                  </div>
                                </details>
                              </div>

                              <div v-if="module.is_completed && (module.completed_class_ids.length > 0)" 
                                   class="mt-3 pt-3 border-top">
                                <details>
                                  <summary class="text-muted small cursor-pointer">
                                    <i class="bi bi-chevron-right me-1"></i>
                                    Ver clases completadas ({{ module.completed_class_ids.length }})
                                  </summary>
                                  <div class="mt-2">
                                    <div class="d-flex flex-wrap gap-1">
                                      <span v-for="classId in module.completed_class_ids" :key="classId"
                                            class="badge bg-success small">
                                        Clase ID: {{ classId }}
                                      </span>
                                    </div>
                                  </div>
                                </details>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Estado sin datos -->
                      <div v-else-if="!loadingModuleProgress" class="text-center py-4 text-muted">
                        <i class="bi bi-info-circle fs-1 mb-3"></i>
                        <p>No hay información de progreso por módulos disponible.</p>
                        <button class="btn btn-sm btn-primary" @click="loadModuleProgress">
                          Cargar Progreso de Módulos
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Botón de reclamar certificado - Solo si es validado por curso -->
                  <div v-if="certificateInfo.validated_by === 'course'" class="claim-certificate mt-5">
                    <div class="d-flex align-items-center gap-3">
                      <button class="btn btn-success" :disabled="!canClaimCertificate || claimingCertificate" @click="claimCertificate">
                        <i class="bi bi-award me-2"></i>
                        {{ claimingCertificate ? 'Cargando...' : 'Reclamar Certificado' }}
                      </button>

                      <div v-if="!canClaimCertificate" class="tooltip-wrapper">
                        <span class="text-success text-decoration-underline cursor-pointer" data-bs-toggle="tooltip"
                          data-bs-placement="bottom" title="Aún no cumple los requisitos para reclamar el certificado.">
                          ¿Por qué no puedo reclamar el certificado?
                        </span>
                      </div>
                    </div>

                    <div v-if="claimedCertificatePayload" class="claimed-certificate mt-4">
                      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <h6 class="mb-0">Tu certificado</h6>
                        <div class="d-flex gap-2">
                          <button
                            v-for="format in certificateFormats"
                            :key="format.key"
                            class="btn btn-sm btn-outline-success"
                            :disabled="downloadingFormat !== null"
                            @click="downloadCertificateAs(format.key)"
                          >
                            <i class="bi bi-download me-1"></i>
                            {{ downloadingFormat === format.key ? 'Generando...' : format.label }}
                          </button>
                        </div>
                      </div>
                      <img
                        v-if="claimedCertificatePayload.type === 'image'"
                        id="claimed-certificate-template"
                        :src="claimedCertificatePayload.image_url"
                        alt="Certificado"
                        class="claimed-certificate-image"
                      />
                      <div v-else id="claimed-certificate-template" v-html="sanitize(claimedCertificatePayload.certificate)"></div>
                    </div>
                  </div>

                  <!-- Mensaje informativo cuando la validación es por módulo -->
                  <div v-else-if="certificateInfo.validated_by === 'module'" class="alert alert-info mt-5">
                    <i class="bi bi-info-circle me-2"></i>
                    Este curso otorga certificados por módulo completado. Descarga los certificados individuales desde la vista "Por Módulos".
                  </div>
                </div>
              </div>
            </div>

            <!-- Estado cuando el curso no tiene certificado disponible -->
            <div v-else-if="certificateInfo && certificateInfo.noCertificate" class="alert-no-certificate p-4">
              <div class="d-flex align-items-center">
                <i class="bi bi-info-circle me-3" style="font-size: 24px; color: #F59E0B;"></i>
                <div>
                  <h6 class="alert-title mb-1">Sin certificado disponible</h6>
                  <p class="alert-desc mb-0">Este curso no cuenta con emisión de certificado digital actualmente.</p>
                </div>
              </div>
            </div>

            <!-- Estado sin información de certificado -->
            <div v-else class="alert-no-certificate p-4">
              <div class="d-flex align-items-center">
                <i class="bi bi-exclamation-circle me-3" style="font-size: 24px; color: #F59E0B;"></i>
                <div>
                  <h6 class="alert-title mb-1">Información no disponible</h6>
                  <p class="alert-desc mb-0">No hay información de certificado disponible para este curso.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { sanitizeHtml } from "@/helpers/sanitizeHtml";
import { CERTIFICATE_FORMATS, downloadCertificateFile } from "@/helpers/certificateExport";

export default {
  data() {
    return {
      products: [],
      coursesWithCertificate: [], // Nueva propiedad para cursos filtrados
      waitSelection: true,
      productSelected: null,
      certificateInfo: null,
      loadingCertificateInfo: false,
      canClaimCertificate: false,
      certificate_exam_progress: 0,
      currentCourseProgress: 0, // Ahora es una variable de estado local
      claimingCertificate: false,
      claimedCertificatePayload: null,
      downloadingFormat: null,
      certificateFormats: CERTIFICATE_FORMATS,

      // Nuevas propiedades para el progreso por módulos
      progressView: 'course', // 'course' o 'modules'
      moduleProgressData: null,
      loadingModuleProgress: false,

      // Nueva propiedad para controlar la descarga de certificados por módulo
      downloadingModuleCertificate: null, // ID del módulo que se está descargando
    };
  },
  computed: {
    ...mapState("course", ["courseProgress"]),
    ...mapGetters("course", ["course"]),

    showVideoProgress() {
      return (
        this.certificateInfo &&
        this.certificateInfo.condition_to_certificate !== 1
      );
    },

    showExamProgress() {
      return (
        this.certificateInfo &&
        (this.certificateInfo.condition_to_certificate === 1 ||
          this.certificateInfo.condition_to_certificate === 2)
      );
    },

    combinedProgress() {
      if (!this.certificateInfo) return 0;

      switch (this.certificateInfo.condition_to_certificate) {
        case 0:
          return this.currentCourseProgress;
        case 1:
          return this.certificate_exam_progress;
        case 2:
          return Math.round(
            (this.currentCourseProgress + this.certificate_exam_progress) / 2
          );
        default:
          return 0;
      }
    },
  },
  methods: {
    onImgError(e) {
      if (e && e.target) {
        e.target.src = require("@/assets/background-login.webp");
      }
    },
    async getCourses() {
      try {
        const { data } = await this.axios.get("/course/purchased-courses");
        this.products = (data && Array.isArray(data.data)) ? data.data : [];
        await this.filterCoursesWithCertificate();
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.products = [];
        this.coursesWithCertificate = [];
      }
    },

    async filterCoursesWithCertificate() {
      this.coursesWithCertificate = [];
      
      console.log("=== FILTRANDO CURSOS CON CERTIFICADO ===");
      console.log("Total de cursos:", this.products.length);
      
      for (const course of this.products) {
        try {
          const response = await this.axios.get(`/course/details/${course.id}`);
          const courseDetails = response.data.data;
          
          // El certificate puede venir como boolean true/false o como number 1/0
          const hasCertificate = courseDetails.certificate === true || courseDetails.certificate === 1;
          
          console.log(`Curso ${course.title}:`, {
            id: course.id,
            certificate: courseDetails.certificate,
            certificateType: typeof courseDetails.certificate,
            hasCertificate: hasCertificate
          });
          
          if (hasCertificate) {
            this.coursesWithCertificate.push(course);
          }
        } catch (error) {
          console.error(`Error checking certificate for course ${course.id}:`, error);
        }
      }
      
      console.log("Cursos con certificado:", this.coursesWithCertificate.length);
      console.log("=====================================");
    },

    async getCourseProgress(courseId) {
      try {
        const response = await this.axios.get(`/marketing/courses/${courseId}/progress`);
        return response.data?.data?.progress || 0;
      } catch (error) {
        console.error("Error fetching course progress:", error);
        return 0;
      }
    },

    // Nuevo método para cambiar la vista de progreso
    setProgressView(view) {
      this.progressView = view;
      
      // Si cambia a vista de módulos y no hay datos, cargarlos automáticamente
      if (view === 'modules' && !this.moduleProgressData && this.productSelected && this.certificateInfo.validated_by === 'module') {
        this.loadModuleProgress();
      }
    },

    // El backend todavía no expone el progreso detallado por módulo para el
    // estudiante; se deja este estado explícito en vez de llamar a un
    // endpoint que no existe.
    async loadModuleProgress() {
      if (!this.productSelected) {
        return;
      }
      this.moduleProgressData = null;
      this.loadingModuleProgress = false;
      window.alert('El progreso detallado por módulos todavía no está disponible.');
    },

    // El backend todavía no expone la generación/descarga de certificados
    // por módulo; se deja este estado explícito en vez de llamar a un
    // endpoint que no existe.
    downloadModuleCertificate() {
      window.alert('La descarga de certificados por módulo todavía no está disponible.');
    },

    async getCertificateInfo(product) {
      this.productSelected = product;
      this.waitSelection = false;

      // Resetear valores
      this.certificateInfo = null;
      this.loadingCertificateInfo = true;
      this.currentCourseProgress = 0;
      this.certificate_exam_progress = 0;
      this.canClaimCertificate = false;
      this.moduleProgressData = null; // Resetear datos de módulos
      this.progressView = 'course'; // Volver a vista por curso
      this.downloadingModuleCertificate = null; // Resetear estado de descarga

      try {
        // Primero verificar si el curso tiene certificado disponible
        const courseDetailsRes = await this.axios.get(`/course/details/${product.id}`);
        const courseDetails = courseDetailsRes.data.data;

        // Si el curso no tiene certificado disponible, mostrar mensaje
        if (courseDetails.certificate !== true && courseDetails.certificate !== 1) {
          this.certificateInfo = { noCertificate: true };
          return;
        }

        const [configRes, examRes, progressRes] = await Promise.all([
          this.axios.get(`/course/certificate/configuration/${product.id}`),
          this.axios.get(`/marketing/courses/${product.id}/exam/list`),
          this.getCourseProgress(product.id)
        ]);

        const config = configRes.data?.data || {};
        let configData = {};
        try {
          configData = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
        } catch (e) {
          configData = {};
        }

        this.certificateInfo = {
          condition_to_certificate: config.condition_to_certificate,
          type_certificate: config.type_certificate,
          validated_by: config.validated_by,
          module_name: configData.module ? `Módulo #${configData.module}` : '',
          lesson_name: configData.lesson ? `Clase #${configData.lesson}` : '',
          certificate_price: configData.certificate_price || 0,
        };
        this.certificate_exam_progress = examRes.data?.data?.exam_progress || 0;
        this.currentCourseProgress = progressRes;

        this.checkCertificateEligibility();
      } catch (error) {
        console.error("Error fetching certificate info:", error);
        this.certificateInfo = null;
      } finally {
        this.loadingCertificateInfo = false;
      }
    },

    async checkCertificateEligibility() {
      try {
        const response = await this.axios.get(
          `/marketing/courses/${this.productSelected.id}/certificate/check`
        );
        this.canClaimCertificate = response.data?.data?.can_claim === true;
      } catch (error) {
        console.error("Error checking certificate eligibility:", error);
        this.canClaimCertificate = false;
      }
    },

    getConditionText(condition) {
      const conditionMap = {
        0: "Visualizar videos",
        1: "Aprobar exámenes",
        2: "Visualizar videos y aprobar exámenes",
      };
      return conditionMap[condition] || "No especificado";
    },

    getValidatedByText(validatedBy) {
      const validationMap = {
        course: "Curso",
        module: "Módulo",
        lesson: "Lección",
      };
      return validationMap[validatedBy] || "No especificado";
    },

    sanitize: sanitizeHtml,

    async claimCertificate() {
      this.claimingCertificate = true;
      this.claimedCertificatePayload = null;

      try {
        const response = await this.axios.get(
          `/marketing/courses/${this.productSelected.id}/certificate/download`
        );

        const payload = response.data?.data;
        if (!payload || (!payload.certificate && !payload.image_url)) {
          window.alert('El certificado todavía no está disponible.');
          return;
        }

        this.claimedCertificatePayload = payload;
        this.$nextTick(() => {
          const el = document.getElementById('claimed-certificate-template');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      } catch (error) {
        const msg = error.response?.data?.message || 'Error al obtener el certificado';
        window.alert(msg);
        console.error(error);
      } finally {
        this.claimingCertificate = false;
      }
    },

    async downloadCertificateAs(format) {
      if (!this.claimedCertificatePayload || this.downloadingFormat) return;

      this.downloadingFormat = format;
      try {
        const baseName = `certificado_${this.productSelected.slug || this.productSelected.id}`;
        await downloadCertificateFile(this.claimedCertificatePayload, format, baseName);
      } catch (error) {
        window.alert(error.message || 'No se pudo descargar el certificado.');
        console.error(error);
      } finally {
        this.downloadingFormat = null;
      }
    },
  },
  mounted() {
    this.getCourses();
  },
};
</script>

<style scoped>
/* Contenedor principal */
.certificate-container {
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Sidebar de cursos y contenedor principal */
.certificate-container {
  min-height: 85vh;
}

.courses-wrapper {
  background: #FAF9F5 !important;
  border: 1px solid #E5E3DC !important;
  border-radius: 24px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03) !important;
  height: calc(100vh - 120px);
  overflow-y: auto;
}

.sidebar-main-title {
  font-family: 'Outfit', sans-serif !important;
  font-weight: 700 !important;
  color: #18181B !important;
  font-size: 1.15rem !important;
}

.border-bottom-subtle {
  border-bottom: 1px solid #E5E3DC !important;
}

/* Tarjetas de Cursos en Sidebar */
.courses-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.course-card-item {
  background: #FFFFFF !important;
  border: 1px solid #E5E3DC !important;
  border-radius: 16px !important;
  padding: 14px 16px !important;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.course-card-item:hover {
  transform: translateY(-2px);
  border-color: var(--primary-color) !important;
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.12) !important;
}

.course-card-item.active {
  background: rgba(16, 185, 129, 0.08) !important;
  border: 2px solid var(--primary-color) !important;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.18) !important;
}

.course-card-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.course-img-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  overflow: hidden;
  background: #E5E3DC;
}

.course-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.course-info-content {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.course-card-title {
  font-family: 'Outfit', sans-serif !important;
  font-weight: 600 !important;
  font-size: 0.92rem !important;
  color: #18181B !important;
  margin: 0 !important;
  line-height: 1.3;
}

.certificate-badge {
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  color: var(--primary-color) !important;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  background: rgba(24, 214, 0, 0.1);
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  line-height: 1.4;
}

.certificate-badge i {
  font-size: 12px;
}

/* Panel Principal de Certificado */
.certificate-content {
  background: #FAF9F5 !important;
  border: 1px solid #E5E3DC !important;
  border-radius: 24px !important;
  min-height: 580px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03) !important;
}

/* Estado Vacío de Selección */
.empty-icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #FFFFFF;
  border: 1px solid #E5E3DC;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.empty-title {
  font-family: 'Outfit', sans-serif !important;
  font-weight: 700 !important;
  font-size: 1.25rem !important;
  color: #18181B !important;
}

.empty-subtitle {
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  font-size: 0.9rem !important;
  color: #71717A !important;
  max-width: 420px;
  margin: 0 auto;
}

/* Header del Curso Seleccionado */
.selected-course-header {
  background: #FFFFFF !important;
  border: 1px solid #E5E3DC !important;
  border-radius: 18px !important;
}

.selected-img-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  overflow: hidden;
  background: #E5E3DC;
}

.selected-course-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.selected-course-title {
  font-family: 'Outfit', sans-serif !important;
  font-weight: 700 !important;
  font-size: 1.2rem !important;
  color: #18181B !important;
}

/* Alerta de No Certificado / Info Warning */
.alert-no-certificate {
  background: #FEF3C7 !important;
  border: 1px solid #FCD34D !important;
  border-radius: 18px !important;
}

.alert-title {
  font-family: 'Outfit', sans-serif !important;
  font-weight: 700 !important;
  color: #92400E !important;
  font-size: 0.98rem !important;
}

.alert-desc {
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  color: #B45309 !important;
  font-size: 0.88rem !important;
}

.info-item {
  background-color: #FFFFFF !important;
  border: 1px solid #E5E3DC !important;
  padding: 1rem;
  border-radius: 14px;
  height: 100%;
}

.progress-wrapper {
  background-color: #FFFFFF !important;
  border: 1px solid #E5E3DC !important;
  padding: 1.5rem;
  border-radius: 14px;
}

.progress-bar {
  background-color: var(--primary-color) !important;
}

.btn-outline-success {
  border-color: var(--primary-color) !important;
  color: var(--primary-color) !important;
}

.btn-outline-success:hover {
  background-color: var(--primary-color) !important;
  color: #FFFFFF !important;
}

.btn-success {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.btn-success:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.btn-success:disabled {
  background-color: #8df380;
  border-color: #8df380;
}

/* Botones de vista */
.btn-group .btn {
  border-radius: 0.375rem;
}

.btn-group .btn:not(:last-child) {
  margin-right: 0.5rem;
}

/* Spinner personalizado */
.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Tooltip */
.tooltip-wrapper {
  cursor: pointer;
}

/* Certificado reclamado (vista previa) */
.claimed-certificate-image {
  max-width: 100%;
  border-radius: 12px;
  border: 1px solid #E5E3DC;
}

/* Estados de carga para botones de módulo */
.bi-hourglass-split {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* Responsive */
@media (max-width: 991.98px) {
  .courses-sidebar {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }

  .courses-wrapper {
    height: auto;
    max-height: 50vh;
  }
  
  .btn-group {
    flex-direction: column;
    width: 100%;
  }
  
  .btn-group .btn {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 767.98px) {
  .certificate-content {
    padding: 1rem !important;
  }

  .selected-course-image {
    aspect-ratio: 21/9;
  }

  .info-item {
    padding: 0.75rem;
  }

  .progress-wrapper {
    padding: 1rem;
  }
  
  .summary-card h3 {
    font-size: 1.5rem;
  }
  
  .module-item .card-body {
    padding: 0.75rem;
  }
  
  .d-flex.align-items-center {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .col-md-6, .col-md-2 {
    width: 100%;
    margin-bottom: 1rem;
  }
  
  .text-end {
    text-align: left !important;
  }
  
  /* Ajustes específicos para móvil en la vista de módulos */
  .module-item .row {
    flex-direction: column;
  }
  
  .module-item .col-md-6,
  .module-item .col-md-2 {
    width: 100%;
    margin-bottom: 0.5rem;
  }
  
  .module-item .text-end {
    text-align: center !important;
    margin-top: 0.5rem;
  }
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.certificate-details {
  animation: fadeIn 0.3s ease-out;
}

.modules-detailed {
  animation: fadeIn 0.5s ease-out;
}

/* Estados de carga */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Mejoras en la accesibilidad */
.btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(26, 232, 0, 0.25);
}

.cursor-pointer {
  cursor: pointer;
}
</style>